import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { subjectColor } from "@/lib/constants";
import ExamTypeToggle from "@/components/ExamTypeToggle";
import { Switch } from "@/components/ui/switch";
import {
  FileSpreadsheet, HelpCircle, KeyRound, ScanLine, QrCode, Trophy, Trash2, PlusCircle, Pencil, Save, Loader2,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS = {
  draft: { label: "Taslak", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  read: { label: "Okunuyor", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  finalized: { label: "Sonuçlandı", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
};

export default function Exams() {
  const [exams, setExams] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => api.get("/exams").then((r) => setExams(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
    api.get("/classes").then((r) => setGrades([...new Set(r.data.map((c) => c.grade))].sort())).catch(() => {});
  }, []);

  const del = async (id) => {
    try { await api.delete(`/exams/${id}`); toast.success("Sınav silindi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [grades, setGrades] = useState([]);
  const [perGrade, setPerGrade] = useState(false);
  const [gradeConfigs, setGradeConfigs] = useState({});

  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/exams/${id}`);
      setEditing(data);
      const gc = data.grade_configs || {};
      setGradeConfigs(gc);
      setPerGrade(Object.keys(gc).length > 0);
      setEditOpen(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setSubjCount = (i, v) => setEditing((p) => ({ ...p, subjects: p.subjects.map((s, idx) => idx === i ? { ...s, question_count: v } : s) }));
  const editTotal = editing ? editing.subjects.reduce((a, s) => a + (parseInt(s.question_count) || 0), 0) : 0;
  const gradeSubs = (g) => (gradeConfigs[g] && gradeConfigs[g].length ? gradeConfigs[g] : (editing ? editing.subjects : []));
  const gradeTotal = (g) => gradeSubs(g).reduce((a, s) => a + (parseInt(s.question_count) || 0), 0);
  const setGradeCount = (g, i, v) => setGradeConfigs((prev) => {
    const base = prev[g] && prev[g].length ? prev[g] : (editing ? editing.subjects : []);
    return { ...prev, [g]: base.map((s, idx) => idx === i ? { ...s, question_count: v } : s) };
  });

  const saveEdit = async () => {
    if (!editing.name.trim()) { toast.error("Sınav adı gerekli"); return; }
    if (editTotal === 0) { toast.error("Toplam soru sayısı 0 olamaz"); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name, exam_no: editing.exam_no, exam_type: editing.exam_type || "KTT", date: editing.date, grade: editing.grade,
        duration: parseInt(editing.duration) || 90, description: editing.description,
        subjects: editing.subjects.map((s) => ({ key: s.key, name: s.name, order: s.order, question_count: parseInt(s.question_count) || 0 })),
        grade_configs: {},
      };
      if (perGrade && grades.length) {
        grades.forEach((g) => { payload.grade_configs[g] = gradeSubs(g).map((s) => ({ key: s.key, name: s.name, order: s.order, question_count: parseInt(s.question_count) || 0 })); });
      }
      await api.put(`/exams/${editing.id}`, payload);
      toast.success("Sınav güncellendi");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!exams) return <div className="text-muted-foreground">Yükleniyor...</div>;

  const TYPE_FILTERS = [{ v: "all", l: "Tümü" }, { v: "KTT", l: "Haftalık KTT" }, { v: "deneme", l: "Deneme" }];
  const shown = exams.filter((e) => typeFilter === "all" || (e.exam_type || "KTT") === typeFilter);

  return (
    <div>
      <PageHeader title="Sınavlar" desc="Oluşturulan tüm sınavlar (KTT ve Deneme)">
        <Button onClick={() => nav("/sinav-olustur")} data-testid="exams-create"><PlusCircle className="h-4 w-4 mr-2" />Sınav Oluştur</Button>
      </PageHeader>

      <div className="inline-flex rounded-full border border-border bg-background p-0.5 mb-5" data-testid="exams-type-filter">
        {TYPE_FILTERS.map((t) => (
          <button key={t.v} type="button" onClick={() => setTypeFilter(t.v)} data-testid={`exams-filter-${t.v}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${typeFilter === t.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={FileSpreadsheet} title="Henüz sınav yok" desc="İlk KTT sınavınızı oluşturarak başlayın.">
          <Button onClick={() => nav("/sinav-olustur")} data-testid="exams-empty-create">Sınav Oluştur</Button>
        </EmptyState>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((e) => {
            const st = STATUS[e.status] || STATUS.draft;
            const isDeneme = (e.exam_type || "KTT") === "deneme";
            return (
              <Card key={e.id} className="p-5 bg-card border-border flex flex-col" data-testid={`exam-card-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-bold tracking-tight">{e.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{e.grade && `${e.grade}. Sınıflar · `}{e.date || "Tarih yok"}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge className={isDeneme ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300" : "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300"} data-testid={`exam-type-badge-${e.id}`}>{isDeneme ? "Deneme" : "KTT"}</Badge>
                    <Badge className={st.cls}>{st.label}</Badge>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(e.id)} data-testid={`exam-edit-${e.id}`} title="Düzenle"><Pencil className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="flex gap-4 mt-4 text-sm">
                  <div><span className="font-bold text-lg">{e.total_questions}</span> <span className="text-muted-foreground text-xs">soru</span></div>
                  <div><span className="font-bold text-lg">{e.result_count}</span> <span className="text-muted-foreground text-xs">sonuç</span></div>
                  <div className="ml-auto">{e.answer_key_filled
                    ? <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">Anahtar ✓</Badge>
                    : <Badge className="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Anahtar eksik</Badge>}</div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => nav(`/sinav/${e.id}/sorular`)} data-testid={`exam-questions-${e.id}`}><HelpCircle className="h-4 w-4 mr-1.5" />Sorular</Button>
                  <Button variant="outline" size="sm" onClick={() => nav(`/sinav/${e.id}/cevap-anahtari`)} data-testid={`exam-key-${e.id}`}><KeyRound className="h-4 w-4 mr-1.5" />Cevap Anahtarı</Button>
                  <Button variant="outline" size="sm" onClick={() => nav(`/sinav/${e.id}/optik-formlar`)} data-testid={`exam-forms-${e.id}`}><ScanLine className="h-4 w-4 mr-1.5" />Optik Form</Button>
                  <Button variant="outline" size="sm" onClick={() => nav(`/sinav/${e.id}/optik-oku`)} data-testid={`exam-read-${e.id}`}><QrCode className="h-4 w-4 mr-1.5" />Optik Oku</Button>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button className="flex-1" size="sm" onClick={() => nav(`/sinav/${e.id}/sonuclar`)} data-testid={`exam-results-${e.id}`}><Trophy className="h-4 w-4 mr-1.5" />Sonuçlar</Button>
                  {user?.role === "admin" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon" className="text-destructive" data-testid={`exam-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-card border-border">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Sınav silinsin mi?</AlertDialogTitle>
                          <AlertDialogDescription>"{e.name}" sınavı, soruları, optikleri ve sonuçları kalıcı olarak silinecek.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(e.id)} className="bg-destructive text-destructive-foreground">Sil</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Sınav düzenleme penceresi */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto ktt-scroll">
          <DialogHeader><DialogTitle>Sınavı Düzenle</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2"><Label>Sınav Türü</Label><ExamTypeToggle value={editing.exam_type || "KTT"} onChange={(v) => setEditing({ ...editing, exam_type: v })} tid="edit-exam-type" /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label>Sınav Adı</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-exam-name" /></div>
                <div className="space-y-1.5"><Label>Sınav No</Label><Input value={editing.exam_no || ""} onChange={(e) => setEditing({ ...editing, exam_no: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Tarih</Label><Input type="date" value={editing.date || ""} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Sınıf Seviyesi</Label><Input value={editing.grade || ""} onChange={(e) => setEditing({ ...editing, grade: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Süre (dk)</Label><Input type="number" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} /></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">Ders Başına Soru Sayısı</Label>
                  <span className="text-xs font-mono text-muted-foreground" data-testid="edit-total">Toplam: {editTotal}</span>
                </div>
                <div className="space-y-2">
                  {[...editing.subjects].sort((a, b) => a.order - b.order).map((s) => {
                    const idx = editing.subjects.findIndex((x) => x.key === s.key);
                    const c = subjectColor(s.key);
                    return (
                      <div key={s.key} className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-background">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${c.badge}`}>{s.name}</span>
                        <Input type="number" min="0" value={s.question_count} onChange={(e) => setSubjCount(idx, e.target.value)} className="w-20 ml-auto font-mono" data-testid={`edit-count-${s.key}`} />
                        <span className="text-xs text-muted-foreground w-10">soru</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded-md px-3 py-2 mt-3">
                  Uyarı: Soru sayısını değiştirirseniz soru numaralandırması kayar; cevap anahtarını ve optik formları güncellemeyi unutmayın.
                </p>
              </div>
              {grades.length > 0 && (
                <div className="border-t border-border pt-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Sınıf seviyesine göre farklı soru sayıları</Label>
                    <Switch checked={perGrade} onCheckedChange={setPerGrade} data-testid="edit-per-grade-toggle" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Açıkken her sınıf seviyesi için ayrı soru sayısı girebilirsiniz. Üstteki liste genel/varsayılan değerdir.</p>
                  {perGrade && (
                    <div className="space-y-3 mt-3">
                      {grades.map((g) => (
                        <div key={g} className="rounded-lg border border-border p-3 bg-background">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm">{g}. Sınıf</span>
                            <span className="text-xs font-mono text-muted-foreground" data-testid={`edit-grade-total-${g}`}>Toplam: {gradeTotal(g)}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {[...gradeSubs(g)].sort((a, b) => a.order - b.order).map((s) => {
                              const idx = gradeSubs(g).findIndex((x) => x.key === s.key);
                              const c = subjectColor(s.key);
                              return (
                                <div key={s.key} className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${c.badge}`}>{s.name}</span>
                                  <Input type="number" min="0" value={s.question_count} onChange={(e) => setGradeCount(g, idx, e.target.value)} className="w-16 ml-auto font-mono h-8" data-testid={`edit-grade-count-${g}-${s.key}`} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Vazgeç</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-exam-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
